import { useState, useEffect } from 'react'
import {
  Button,
  Flex,
  Input,
  Text,
  Textarea,
  Grid,
  GridItem,
  Tabs,
  Switch,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Building2, User, Banknote, FileText } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  useCreateSupplier,
  useUpdateSupplier,
  useSupplierCategories,
} from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type {
  SupplierWithCategory,
  SupplierUpdateData,
  VatRegime,
  SupplierPaymentMode,
} from '../types'
import {
  VAT_REGIME_LABELS,
  SUPPLIER_PAYMENT_MODE_LABELS,
} from '../types'

interface SupplierModalProps {
  isOpen: boolean
  onClose: () => void
  agencyId: string
  supplier?: SupplierWithCategory | null
}

type FormData = {
  // Informations principales
  name: string
  trade_name: string
  legal_form: string
  siret: string
  vat_number: string
  vat_regime: VatRegime
  activity_code: string
  category_id: string
  is_active: boolean
  notes: string
  // Coordonnées
  address_line1: string
  address_line2: string
  postal_code: string
  city: string
  country: string
  phone: string
  mobile: string
  email: string
  website: string
  // Informations bancaires
  bank_name: string
  iban: string
  bic: string
  payment_mode: SupplierPaymentMode
  payment_delay: number
}

const initialFormData: FormData = {
  name: '',
  trade_name: '',
  legal_form: '',
  siret: '',
  vat_number: '',
  vat_regime: 'NORMAL',
  activity_code: '',
  category_id: '',
  is_active: true,
  notes: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'France',
  phone: '',
  mobile: '',
  email: '',
  website: '',
  bank_name: '',
  iban: '',
  bic: '',
  payment_mode: 'VIREMENT',
  payment_delay: 30,
}

export function SupplierModal({
  isOpen,
  onClose,
  agencyId,
  supplier,
}: SupplierModalProps) {
  const isEditMode = !!supplier
  const createSupplier = useCreateSupplier()
  const updateSupplier = useUpdateSupplier()
  const { data: categories } = useSupplierCategories(agencyId)

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [activeTab, setActiveTab] = useState('general')

  // Initialiser le formulaire en mode édition
  useEffect(() => {
    if (isOpen && supplier) {
      setFormData({
        name: supplier.name || '',
        trade_name: supplier.trade_name || '',
        legal_form: supplier.legal_form || '',
        siret: supplier.siret || '',
        vat_number: supplier.vat_number || '',
        vat_regime: supplier.vat_regime || 'NORMAL',
        activity_code: supplier.activity_code || '',
        category_id: supplier.category_id || '',
        is_active: supplier.is_active ?? true,
        notes: supplier.notes || '',
        address_line1: supplier.address_line1 || '',
        address_line2: supplier.address_line2 || '',
        postal_code: supplier.postal_code || '',
        city: supplier.city || '',
        country: supplier.country || 'France',
        phone: supplier.phone || '',
        mobile: supplier.mobile || '',
        email: supplier.email || '',
        website: supplier.website || '',
        bank_name: supplier.bank_name || '',
        iban: supplier.iban || '',
        bic: supplier.bic || '',
        payment_mode: supplier.payment_mode || 'VIREMENT',
        payment_delay: supplier.payment_delay ?? 30,
      })
    } else if (isOpen && !supplier) {
      setFormData(initialFormData)
    }
  }, [isOpen, supplier])

  const handleClose = () => {
    setFormData(initialFormData)
    setActiveTab('general')
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toaster.create({
        title: 'Erreur',
        description: 'La raison sociale est obligatoire',
        type: 'error',
      })
      return
    }

    try {
      const supplierData = {
        agency_id: agencyId,
        name: formData.name.trim(),
        trade_name: formData.trade_name.trim() || null,
        legal_form: formData.legal_form.trim() || null,
        siret: formData.siret.trim() || null,
        vat_number: formData.vat_number.trim() || null,
        vat_regime: formData.vat_regime,
        activity_code: formData.activity_code.trim() || null,
        category_id: formData.category_id || null,
        is_active: formData.is_active,
        notes: formData.notes.trim() || null,
        address_line1: formData.address_line1.trim() || null,
        address_line2: formData.address_line2.trim() || null,
        postal_code: formData.postal_code.trim() || null,
        city: formData.city.trim() || null,
        country: formData.country.trim() || 'France',
        phone: formData.phone.trim() || null,
        mobile: formData.mobile.trim() || null,
        email: formData.email.trim() || null,
        website: formData.website.trim() || null,
        bank_name: formData.bank_name.trim() || null,
        iban: formData.iban.trim().replace(/\s/g, '') || null,
        bic: formData.bic.trim().replace(/\s/g, '') || null,
        payment_mode: formData.payment_mode,
        payment_delay: formData.payment_delay,
      }

      if (isEditMode && supplier) {
        // Exclure agency_id pour la mise à jour
        const { agency_id: _, ...updateData } = supplierData
        await updateSupplier.mutateAsync({
          id: supplier.id,
          data: updateData as SupplierUpdateData,
        })
        toaster.create({
          title: 'Succès',
          description: 'Fournisseur modifié avec succès',
          type: 'success',
        })
      } else {
        await createSupplier.mutateAsync(supplierData)
        toaster.create({
          title: 'Succès',
          description: 'Fournisseur créé avec succès',
          type: 'success',
        })
      }

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'enregistrement",
        type: 'error',
      })
    }
  }

  const isPending = createSupplier.isPending || updateSupplier.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Tabs.Root value={activeTab} onValueChange={(e) => setActiveTab(e.value)}>
              <Tabs.List mb={4}>
                <Tabs.Trigger value="general">
                  <Building2 size={16} />
                  <Text ml={2}>Général</Text>
                </Tabs.Trigger>
                <Tabs.Trigger value="contact">
                  <User size={16} />
                  <Text ml={2}>Coordonnées</Text>
                </Tabs.Trigger>
                <Tabs.Trigger value="banking">
                  <Banknote size={16} />
                  <Text ml={2}>Bancaire</Text>
                </Tabs.Trigger>
                <Tabs.Trigger value="notes">
                  <FileText size={16} />
                  <Text ml={2}>Notes</Text>
                </Tabs.Trigger>
              </Tabs.List>

              {/* Onglet Général */}
              <Tabs.Content value="general">
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={2}>
                    <Field label="Raison sociale" required>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Nom de l'entreprise"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Nom commercial">
                      <Input
                        value={formData.trade_name}
                        onChange={(e) => handleChange('trade_name', e.target.value)}
                        placeholder="Nom d'enseigne"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Forme juridique">
                      <Input
                        value={formData.legal_form}
                        onChange={(e) => handleChange('legal_form', e.target.value)}
                        placeholder="SARL, SAS, etc."
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="SIRET">
                      <Input
                        value={formData.siret}
                        onChange={(e) => handleChange('siret', e.target.value)}
                        placeholder="12345678901234"
                        maxLength={14}
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="N° TVA">
                      <Input
                        value={formData.vat_number}
                        onChange={(e) => handleChange('vat_number', e.target.value)}
                        placeholder="FR12345678901"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Régime TVA">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.vat_regime}
                          onChange={(e) => handleChange('vat_regime', e.target.value as VatRegime)}
                        >
                          {Object.entries(VAT_REGIME_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Code APE/NAF">
                      <Input
                        value={formData.activity_code}
                        onChange={(e) => handleChange('activity_code', e.target.value)}
                        placeholder="6831Z"
                      />
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Field label="Catégorie">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.category_id}
                          onChange={(e) => handleChange('category_id', e.target.value)}
                        >
                          <option value="">Aucune catégorie</option>
                          {categories?.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Flex align="center" gap={3}>
                      <Switch.Root
                        checked={formData.is_active}
                        onCheckedChange={(e) => handleChange('is_active', e.checked)}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                      <Text>Fournisseur actif</Text>
                    </Flex>
                  </GridItem>
                </Grid>
              </Tabs.Content>

              {/* Onglet Coordonnées */}
              <Tabs.Content value="contact">
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={2}>
                    <Field label="Adresse">
                      <Input
                        value={formData.address_line1}
                        onChange={(e) => handleChange('address_line1', e.target.value)}
                        placeholder="N° et nom de rue"
                      />
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Field label="Complément d'adresse">
                      <Input
                        value={formData.address_line2}
                        onChange={(e) => handleChange('address_line2', e.target.value)}
                        placeholder="Bâtiment, étage, etc."
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Code postal">
                      <Input
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value)}
                        placeholder="75001"
                        maxLength={5}
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Ville">
                      <Input
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        placeholder="Paris"
                      />
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Field label="Pays">
                      <Input
                        value={formData.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        placeholder="France"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Téléphone">
                      <Input
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="01 23 45 67 89"
                        type="tel"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Mobile">
                      <Input
                        value={formData.mobile}
                        onChange={(e) => handleChange('mobile', e.target.value)}
                        placeholder="06 12 34 56 78"
                        type="tel"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Email">
                      <Input
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="contact@entreprise.fr"
                        type="email"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Site web">
                      <Input
                        value={formData.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                        placeholder="www.entreprise.fr"
                        type="url"
                      />
                    </Field>
                  </GridItem>
                </Grid>
              </Tabs.Content>

              {/* Onglet Bancaire */}
              <Tabs.Content value="banking">
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={2}>
                    <Field label="Nom de la banque">
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => handleChange('bank_name', e.target.value)}
                        placeholder="Nom de l'établissement bancaire"
                      />
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Field label="IBAN">
                      <Input
                        value={formData.iban}
                        onChange={(e) => handleChange('iban', e.target.value)}
                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                        fontFamily="mono"
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="BIC/SWIFT">
                      <Input
                        value={formData.bic}
                        onChange={(e) => handleChange('bic', e.target.value)}
                        placeholder="BNPAFRPP"
                        fontFamily="mono"
                        maxLength={11}
                      />
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Mode de paiement">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.payment_mode}
                          onChange={(e) =>
                            handleChange('payment_mode', e.target.value as SupplierPaymentMode)
                          }
                        >
                          {Object.entries(SUPPLIER_PAYMENT_MODE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </GridItem>

                  <GridItem>
                    <Field label="Délai de paiement (jours)">
                      <Input
                        type="number"
                        value={formData.payment_delay}
                        onChange={(e) => handleChange('payment_delay', parseInt(e.target.value) || 0)}
                        min={0}
                        max={180}
                      />
                    </Field>
                  </GridItem>
                </Grid>
              </Tabs.Content>

              {/* Onglet Notes */}
              <Tabs.Content value="notes">
                <Field label="Notes internes">
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Informations complémentaires sur ce fournisseur..."
                    rows={8}
                  />
                </Field>
              </Tabs.Content>
            </Tabs.Root>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isPending}
              disabled={!formData.name.trim()}
            >
              {isEditMode ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
